/**
 * SEO utilities for managing document title and meta tags
 */

import { SeoComponent_Plain } from "../types/components/page/SeoComponent";

const DEFAULT_DESCRIPTION =
  "D.TRADING \u2013 a global commodity trading company specializing in energy products, agricultural commodities, and metals.";

/**
 * Update document title and meta tags
 */
export const updateSEO = (
  seo?: SeoComponent_Plain,
  defaultTitle = "D.TRADING",
) => {
  // Update title
  if (seo?.metaTitle) {
    document.title = seo.metaTitle;
  } else {
    document.title = defaultTitle;
  }

  // Update or create meta description (with fallback)
  const description = seo?.metaDescription || DEFAULT_DESCRIPTION;
  updateMetaTag("description", description);
  updateMetaTag("og:description", description, "property");
  updateMetaTag("twitter:description", description);

  // Update og:title and twitter:title
  if (seo?.metaTitle) {
    updateMetaTag("og:title", seo.metaTitle, "property");
    updateMetaTag("twitter:title", seo.metaTitle);
  }

  // Update og:image and twitter:image
  if (seo?.metaImage?.url) {
    updateMetaTag("og:image", seo.metaImage.url, "property");
    updateMetaTag("twitter:image", seo.metaImage.url);
  }

  // Update og:url and twitter:url (self-referencing)
  const currentUrl =
    window.location.href.split("?")[0].replace(/\/$/, "") ||
    window.location.href;
  updateMetaTag("og:url", currentUrl, "property");
  updateMetaTag("twitter:url", currentUrl);

  // Update canonical URL (always self-referencing)
  updateCanonicalURL();

  // Update custom meta tags
  if (seo?.metaCustom && Array.isArray(seo.metaCustom)) {
    seo.metaCustom.forEach((meta) => {
      if (meta?.name && meta?.content) {
        updateMetaTag(meta.name, meta.content);
      }
    });
  }
};

/**
 * Update or create a meta tag
 */
const updateMetaTag = (
  name: string,
  content: string,
  attribute: "name" | "property" = "name",
) => {
  let element = document.querySelector(
    `meta[${attribute}="${name}"]`,
  ) as HTMLMetaElement;

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }

  element.content = content;
};

/**
 * Reset SEO to defaults
 */
export const resetSEO = () => {
  document.title = "D.TRADING";
};

/**
 * Update or create canonical URL link tag
 * Always uses the current page URL (self-referencing) to ensure proper SEO
 */
export const updateCanonicalURL = () => {
  let linkElement = document.querySelector(
    'link[rel="canonical"]',
  ) as HTMLLinkElement;

  // Always generate canonical URL from current location (self-referencing)
  const { protocol, hostname, pathname, port } = window.location;

  // Remove trailing slash (except for root path)
  const cleanPathname =
    pathname === "/" ? pathname : pathname.replace(/\/$/, "");

  // Build full URL with protocol
  const portPart = port && port !== "80" && port !== "443" ? `:${port}` : "";
  const url = `${protocol}//${hostname}${portPart}${cleanPathname}`;

  if (!linkElement) {
    linkElement = document.createElement("link");
    linkElement.setAttribute("rel", "canonical");
    document.head.appendChild(linkElement);
  }

  linkElement.href = url;
};
